import { useEffect } from 'react'

const LINK_PREFIX = 'brand-font-'

const linkId = (family: string) => LINK_PREFIX + family.toLowerCase().replace(/[^a-z0-9]+/g, '-')

/**
 * Best-effort Google Fonts loader for the Brand Standards section: injects one
 * stylesheet link per hand-entered font name so the name can render in its own
 * typeface. Unknown/custom families (or no network) simply fail the stylesheet
 * fetch and the text falls back to the packet's sans stack — no error surface.
 * Links are deduped globally and intentionally never removed: fonts persist
 * across Edit/Present remounts within the session.
 */
export function useBrandFonts(fonts: string[]) {
  // Effect dep is the joined value so a same-content array doesn't re-run it.
  const joined = fonts
    .map(f => f.trim())
    .filter(Boolean)
    .join('|')

  useEffect(() => {
    if (!joined) return
    for (const family of joined.split('|')) {
      const id = linkId(family)
      if (document.getElementById(id)) continue
      const link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      link.href =
        'https://fonts.googleapis.com/css2?family=' +
        encodeURIComponent(family).replace(/%20/g, '+') +
        '&display=swap'
      document.head.appendChild(link)
    }
  }, [joined])
}

/** CSS font-family stack for a brand font name, falling back to the given stack. */
export const brandFontStack = (family: string, fallback: string) => `"${family}", ${fallback}`
